export type AnimationFrameWaiter = () => Promise<void>;
export type TimeReader = () => number;
export interface TimeoutDeadline {
  promise: Promise<void>;
  cancel: () => void;
}
export type TimeoutDeadlineFactory = (milliseconds: number) => TimeoutDeadline;

const NEXT_FRAME_FALLBACK_MS = 100;
const DEFAULT_RENDERER_WAIT_MS = 7000;
// RendererWarmup's adaptive DPR controller samples 1.5 seconds at a time. Keep
// the cover up for substantially longer than one full sample so first-use shader,
// texture, render-target, and quality work can finish before gameplay is exposed.
const MARKED_RENDERER_SETTLE_MS = 2300;
const MARKED_RENDERER_MAX_FRAME_GAP_MS = 50;
const MARKED_RENDERER_MIN_SETTLE_FRAMES = 12;

export function nextAnimationFrame(): Promise<void> {
  return new Promise((resolve) => {
    if (typeof requestAnimationFrame !== 'function') {
      globalThis.setTimeout(resolve, 0);
      return;
    }

    let settled = false;
    let frameId: number | null = null;
    let timeoutId: ReturnType<typeof globalThis.setTimeout> | undefined;
    const finish = (cancelPendingFrame: boolean) => {
      if (settled) return;
      settled = true;
      if (timeoutId !== undefined) globalThis.clearTimeout(timeoutId);
      timeoutId = undefined;
      if (cancelPendingFrame && frameId !== null && typeof cancelAnimationFrame === 'function') {
        try {
          cancelAnimationFrame(frameId);
        } catch {
          // A non-standard frame implementation can fail cancellation after the
          // timer fallback wins. The wait has already completed safely.
        }
      }
      resolve();
    };

    timeoutId = globalThis.setTimeout(() => finish(true), NEXT_FRAME_FALLBACK_MS);
    try {
      frameId = requestAnimationFrame(() => finish(false));
    } catch {
      finish(false);
    }
  });
}

function currentTime(): number {
  return typeof performance === 'undefined' ? Date.now() : performance.now();
}

function createTimeoutDeadline(milliseconds: number): TimeoutDeadline {
  let timeoutId: ReturnType<typeof globalThis.setTimeout> | undefined;
  const promise = new Promise<void>((resolve) => {
    timeoutId = globalThis.setTimeout(resolve, Math.max(0, milliseconds));
  });
  return {
    promise,
    cancel: () => {
      if (timeoutId !== undefined) globalThis.clearTimeout(timeoutId);
      timeoutId = undefined;
    },
  };
}

/**
 * Wait until a mounted renderer canvas has a real backing buffer and, when the
 * 3D renderer publishes a warmup marker, until its shader/material compilation
 * has completed and the browser has demonstrated a short uninterrupted frame
 * window. Keeping the loading cover over that settling window lets Three finish
 * first-use GPU work and gives the adaptive DPR controller a complete first
 * performance sample before gameplay input and camera motion become visible.
 * A backing-buffer resize (normally an adaptive-DPR change) restarts stability
 * so the new render target also has to demonstrate settled frames before reveal.
 *
 * Renderers without the 3D marker keep the lightweight readiness behavior used
 * by 2D/tests, with one extra stable frame so a just-mounted 3D canvas has time
 * to publish its warmup marker before the gate opens.
 *
 * The frame budget remains the primary fail-open guard. A single absolute
 * elapsed-time deadline also races every frame wait so a hidden/throttled tab
 * cannot deadlock startup when requestAnimationFrame stops firing entirely.
 * An optional abort signal lets lifecycle wrappers terminate an obsolete wait
 * immediately instead of leaving its frame/deadline loop alive in the background.
 */
export async function waitForRendererCanvas(
  root: Pick<HTMLElement, 'querySelector'> | null,
  maxFrames = 360,
  waitForFrame: AnimationFrameWaiter = nextAnimationFrame,
  maxWaitMs = DEFAULT_RENDERER_WAIT_MS,
  now: TimeReader = currentTime,
  createDeadline: TimeoutDeadlineFactory = createTimeoutDeadline,
  signal?: AbortSignal,
): Promise<boolean> {
  let stableCanvas: HTMLCanvasElement | null = null;
  let stableWidth = 0;
  let stableHeight = 0;
  let unmarkedStableFrames = 0;
  let markedStableSince: number | null = null;
  let markedLastFrameAt: number | null = null;
  let markedStableFrames = 0;
  const startedAt = now();
  const timedOut = () => now() - startedAt >= maxWaitMs;
  const deadline = createDeadline(maxWaitMs);
  const deadlineResult = deadline.promise.then(() => false);
  let onAbort: (() => void) | undefined;
  const abortResult = signal
    ? new Promise<boolean>((resolve) => {
        onAbort = () => resolve(false);
        if (signal.aborted) resolve(false);
        else signal.addEventListener('abort', onAbort, { once: true });
      })
    : null;
  const resetMarkedStability = () => {
    markedStableSince = null;
    markedLastFrameAt = null;
    markedStableFrames = 0;
  };
  const resetCanvasStability = (canvas: HTMLCanvasElement | null) => {
    stableCanvas = canvas;
    stableWidth = canvas?.width ?? 0;
    stableHeight = canvas?.height ?? 0;
    unmarkedStableFrames = 0;
    resetMarkedStability();
  };
  const waitForBoundedFrame = async (): Promise<boolean> => {
    if (signal?.aborted || timedOut()) return false;
    let frameResult: Promise<boolean>;
    try {
      frameResult = waitForFrame().then(
        () => !signal?.aborted,
        () => false,
      );
    } catch {
      // Renderer readiness is an optimization gate, not a gameplay dependency.
      // A non-standard frame waiter that throws synchronously must fail open just
      // like a rejected/throttled frame rather than rejecting table startup.
      return false;
    }
    const waits: Promise<boolean>[] = [frameResult, deadlineResult];
    if (abortResult) waits.push(abortResult);
    return Promise.race(waits);
  };

  try {
    for (let frame = 0; frame < maxFrames; frame += 1) {
      if (!await waitForBoundedFrame()) return false;
      if (signal?.aborted || timedOut()) return false;

      const canvas = root?.querySelector('canvas') as HTMLCanvasElement | null;
      if (!canvas || canvas.width <= 1 || canvas.height <= 1) {
        resetCanvasStability(null);
        continue;
      }

      // Snapshot dimensions before comparing canvas identity. TypeScript cannot
      // infer assignments performed inside resetCanvasStability(), and keeping
      // this independent also makes the same-canvas resize condition explicit.
      const dimensionsChanged = canvas.width !== stableWidth || canvas.height !== stableHeight;
      if (canvas !== stableCanvas || dimensionsChanged) {
        // setDpr() keeps the same DOM canvas but resizes its backing buffer.
        // Treat that exactly like a replacement: first-use allocations for the
        // new target must settle behind the cover instead of leaking into play.
        resetCanvasStability(canvas);
      }

      const rendererReady = canvas.dataset?.rendererReady;
      if (rendererReady === 'false') {
        unmarkedStableFrames = 0;
        resetMarkedStability();
        continue;
      }

      if (rendererReady === 'true') {
        const frameAt = now();
        const gap = markedLastFrameAt === null ? 0 : frameAt - markedLastFrameAt;
        if (
          markedStableSince === null
          || markedLastFrameAt === null
          || gap < 0
          || gap > MARKED_RENDERER_MAX_FRAME_GAP_MS
        ) {
          markedStableSince = frameAt;
          markedStableFrames = 1;
        } else {
          markedStableFrames += 1;
        }
        markedLastFrameAt = frameAt;

        if (
          frameAt - markedStableSince < MARKED_RENDERER_SETTLE_MS
          || markedStableFrames < MARKED_RENDERER_MIN_SETTLE_FRAMES
        ) continue;

        const presentedCanvas = root?.querySelector('canvas') as HTMLCanvasElement | null;
        if (
          presentedCanvas === canvas
          && presentedCanvas.width === stableWidth
          && presentedCanvas.height === stableHeight
          && presentedCanvas.width > 1
          && presentedCanvas.height > 1
          && presentedCanvas.dataset?.rendererReady === 'true'
        ) return true;

        resetCanvasStability(presentedCanvas);
        continue;
      }

      resetMarkedStability();
      unmarkedStableFrames += 1;
      if (unmarkedStableFrames < 2) continue;

      if (!await waitForBoundedFrame()) return false;
      if (signal?.aborted || timedOut()) return false;

      const presentedCanvas = root?.querySelector('canvas') as HTMLCanvasElement | null;
      if (
        presentedCanvas !== canvas
        || presentedCanvas.width !== stableWidth
        || presentedCanvas.height !== stableHeight
        || presentedCanvas.width <= 1
        || presentedCanvas.height <= 1
        || presentedCanvas.dataset?.rendererReady === 'false'
      ) {
        resetCanvasStability(presentedCanvas);
        continue;
      }
      return true;
    }
    return false;
  } finally {
    // Successful/cancelled 3D startup normally completes before the bounded
    // fail-open deadline. Cancel that now-useless timer instead of retaining the
    // closure and waking the event loop later for every table/view transition.
    deadline.cancel();
    if (signal && onAbort) signal.removeEventListener('abort', onAbort);
  }
}
